import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { sessionStorage as storage } from '@/lib/storage';
import { authService, passkeyService } from '@/api/services';
import { getPasskeyErrorMessage } from '@/lib/errors/passkey-error';

// localStorage flag (per username) for the "Don't ask again" passkey nudge.
const PASSKEY_PROMPT_DISMISSED_PREFIX = 'na_passkey_prompt_dismissed:';
// localStorage flag (per username) recording that THIS browser/device already has
// a passkey — set when one is enrolled here or used to sign in here. Drives the
// per-device nudge: a passkey on another device must not suppress the prompt here.
const PASSKEY_ENROLLED_PREFIX = 'na_passkey_enrolled:';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  username: string | null;
  accountUUID: string | null;
  termsAccepted: boolean;
  error: string | null;
  /**
   * Seconds remaining before another login attempt is allowed, set only when
   * `error` is the backend's 429 "too many failed attempts" response — the
   * login form uses this to render a countdown instead of just static text.
   */
  retryAfterSeconds: number | null;
}

export interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<{ success: boolean; termsAccepted: boolean }>;
  loginWithPasskey: () => Promise<{ success: boolean; termsAccepted: boolean }>;
  logout: () => void;
  setTermsAccepted: (accepted: boolean) => void;
  clearError: () => void;
  // Post-login nudge to enroll a passkey — shown per-device whenever this browser
  // doesn't have one yet (not gated on account-wide passkey count).
  shouldPromptPasskeyEnrollment: boolean;
  dismissPasskeyPrompt: (remember: boolean) => void;
  // Marks this device as having a passkey (call after enrolling one here).
  notePasskeyEnrolled: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: storage.isAuthenticated(),
    isLoading: false,
    username: storage.getUserID(),
    accountUUID: storage.getAccountUUID(),
    termsAccepted: storage.getTermsAccepted(),
    error: null,
    retryAfterSeconds: null,
  });
  const [shouldPromptPasskeyEnrollment, setShouldPromptPasskeyEnrollment] = useState(false);

  // After a password login, nudge the user to set up a passkey if their browser
  // supports them, they haven't dismissed the nudge, and they have none yet.
  // Fire-and-forget — never block or fail login on this check.
  const evaluatePasskeyPrompt = useCallback((userID: string) => {
    if (!browserSupportsWebAuthn()) return;
    if (window.localStorage.getItem(`${PASSKEY_PROMPT_DISMISSED_PREFIX}${userID}`)) return;
    // Prompt once per DEVICE, not per account: offer to set up a passkey whenever
    // THIS browser doesn't have one yet — even if the user already enrolled
    // passkeys on other devices. The marker is set on enrollment here and on a
    // passkey sign-in here (notePasskeyEnrolled / loginWithPasskey).
    if (window.localStorage.getItem(`${PASSKEY_ENROLLED_PREFIX}${userID}`)) return;
    setShouldPromptPasskeyEnrollment(true);
  }, []);

  const dismissPasskeyPrompt = useCallback((remember: boolean) => {
    setShouldPromptPasskeyEnrollment(false);
    if (remember && state.username) {
      window.localStorage.setItem(`${PASSKEY_PROMPT_DISMISSED_PREFIX}${state.username}`, 'true');
    }
  }, [state.username]);

  // Record that this device now has a passkey (call after enrolling one here) so
  // the per-device nudge won't fire again on this browser.
  const notePasskeyEnrolled = useCallback(() => {
    const uid = storage.getUserID();
    if (uid) {
      window.localStorage.setItem(`${PASSKEY_ENROLLED_PREFIX}${uid}`, 'true');
    }
    setShouldPromptPasskeyEnrollment(false);
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<{ success: boolean; termsAccepted: boolean }> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null, retryAfterSeconds: null }));

    try {
      const result = await authService.authenticate(username, password);

      // Store JWT token and user info
      storage.setAccessToken(result.accessToken);
      storage.setAccountUUID(result.accountUUID);
      storage.setUserID(result.userID);
      storage.setTermsAccepted(result.termsAndConditionsAccepted);

      setState({
        isAuthenticated: true,
        isLoading: false,
        username: result.userID,
        accountUUID: result.accountUUID,
        termsAccepted: result.termsAndConditionsAccepted,
        error: null,
        retryAfterSeconds: null,
      });
      // Nudge passkey enrollment (only for password logins — a passkey login
      // means they already have one).
      evaluatePasskeyPrompt(result.userID);
      return { success: true, termsAccepted: result.termsAndConditionsAccepted };
    } catch (err) {
      // The Authenticate proxy returns 401 for any rejected credentials, and 429
      // once the account is throttled after repeated failures (see
      // AccountAuthenticate.py / _login_throttle.py). Treat any other 4xx as a
      // generic credentials problem; never surface a raw 5xx/502 status to the
      // user — show a generic retry message instead.
      let message = 'Unable to sign in right now. Please try again.';
      let retryAfterSeconds: number | null = null;
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 429) {
          retryAfterSeconds = typeof detail?.retryAfterSeconds === 'number' ? detail.retryAfterSeconds : null;
          message = typeof detail?.message === 'string'
            ? detail.message
            : 'Too many failed attempts. Please wait and try again.';
        } else if (status && status >= 400 && status < 500) {
          message = 'Incorrect username or password.';
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
        retryAfterSeconds,
      }));
      return { success: false, termsAccepted: false };
    }
  }, [evaluatePasskeyPrompt]);

  // Passwordless sign-in: the passkey is the sole factor. Mirrors login()'s
  // success path — the finish endpoint returns the same token shape as
  // /Authenticate, so we persist it identically.
  const loginWithPasskey = useCallback(async (): Promise<{ success: boolean; termsAccepted: boolean }> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null, retryAfterSeconds: null }));

    try {
      const { options, challengeId } = await passkeyService.loginStart();
      const credential = await startAuthentication({ optionsJSON: options });
      const result = await passkeyService.loginFinish({ challengeId, credential });

      storage.setAccessToken(result.accessToken);
      storage.setAccountUUID(result.accountUUID);
      storage.setUserID(result.userID);
      storage.setTermsAccepted(result.termsAndConditionsAccepted);

      setState({
        isAuthenticated: true,
        isLoading: false,
        username: result.userID,
        accountUUID: result.accountUUID,
        termsAccepted: result.termsAndConditionsAccepted,
        error: null,
        retryAfterSeconds: null,
      });
      // This device just signed in with a passkey, so it has one — mark it so the
      // per-device enrollment nudge won't fire here on a later password login.
      window.localStorage.setItem(`${PASSKEY_ENROLLED_PREFIX}${result.userID}`, 'true');
      return { success: true, termsAccepted: result.termsAndConditionsAccepted };
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: getPasskeyErrorMessage(err),
        retryAfterSeconds: null,
      }));
      return { success: false, termsAccepted: false };
    }
  }, []);

  const logout = useCallback(() => {
    storage.clearAuth();
    setShouldPromptPasskeyEnrollment(false);
    setState({
      isAuthenticated: false,
      isLoading: false,
      username: null,
      accountUUID: null,
      termsAccepted: false,
      error: null,
      retryAfterSeconds: null,
    });
  }, []);

  const setTermsAccepted = useCallback((accepted: boolean) => {
    storage.setTermsAccepted(accepted);
    setState((prev) => ({ ...prev, termsAccepted: accepted }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null, retryAfterSeconds: null }));
  }, []);

  // Listen for forced logout from HTTP interceptor (token refresh failed)
  useEffect(() => {
    const handleAuthCleared = () => {
      setShouldPromptPasskeyEnrollment(false);
      setState({
        isAuthenticated: false,
        isLoading: false,
        username: null,
        accountUUID: null,
        termsAccepted: false,
        error: 'Session expired. Please log in again.',
        retryAfterSeconds: null,
      });
    };

    window.addEventListener('auth-cleared', handleAuthCleared);
    return () => window.removeEventListener('auth-cleared', handleAuthCleared);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      loginWithPasskey,
      logout,
      setTermsAccepted,
      clearError,
      shouldPromptPasskeyEnrollment,
      dismissPasskeyPrompt,
      notePasskeyEnrolled,
    }),
    [state, login, loginWithPasskey, logout, setTermsAccepted, clearError, shouldPromptPasskeyEnrollment, dismissPasskeyPrompt, notePasskeyEnrolled],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
